import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CameraResultType, Camera } from '@capacitor/core';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeAll, switchMap, take } from 'rxjs/operators';
import * as moment from 'moment';
import * as uuidV4 from 'uuid/v4';
import { PatientDocumentVo } from 'api-clinician-app';
import { b64toBlob, blobToFile } from '@app/shared/functions/b64ToBlob';
import { InvertPreviewCamera } from '@app/shared/functions/camera-invert';
import {
  AddFilesDialogData,
  AddFileDialogComponent,
  AddFilesReturnData,
} from '@app/modules/sidebar/add-file-dialog/add-file-dialog.component';
import { ClinicalEncounterService } from './clinical-encounter.service';

export interface UploadEncounterParams {
  tenantId: string;
  patientId: string;
  // Document uploads (whiteboard, files, photos) hit the legacy mps-api patientdocument
  // endpoint scoped by PracticeId, which can differ from the Meraki tenantId used for
  // encounter ops. Defaults to tenantId for callers that don't distinguish the two.
  documentTenantId?: string;
}

@Injectable({ providedIn: 'root' })
export class AddFileUploadService {
  constructor(private dialog: MatDialog, private snackBar: MatSnackBar, private clinicalEncounterService: ClinicalEncounterService) {}

  takePicture(): Observable<File> {
    InvertPreviewCamera();
    return from(
      Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64,
      })
    ).pipe(
      map(image => b64toBlob(image.base64String, image.format)),
      map(blob => blobToFile(blob, `Picture-${moment().format('YYYYMMDD-HHmmss')}.${blob.type}`)),
      take(1),
      catchError(() => of(null))
    );
  }

  openAddFileDialog(params: UploadEncounterParams, dialogData: AddFilesDialogData = {} as AddFilesDialogData): Observable<unknown> {
    return this.dialog
      .open(AddFileDialogComponent, { data: dialogData })
      .afterClosed()
      .pipe(
        take(1),
        filter((upload: AddFilesReturnData) => !!upload && !!upload.Files && upload.Files.length > 0),
        switchMap((upload: AddFilesReturnData) => {
          const documentTenantId = params.documentTenantId || params.tenantId;
          const uploads$ = upload.Files.map(file => {
            const documentVo: PatientDocumentVo = {
              PracticeId: documentTenantId,
              PatientId: params.patientId,

              Description: upload.Description,
              Category: upload.Category,
              FileName: file.name,
              CapturedDate: upload.CapturedDate,
              DocumentDate: upload.DocumentDate,
              FileSize: file.size,
              DocumentId: uuidV4(),

              Notes: upload.Notes,
              AdditionalData: upload.AdditionalData,

              CreatedBy: null, // TODO
              MimeType: null, // TODO

              ThumbnailUrl: null, //// ignore this field
              DocumentUrl: null, //// ignore this field
            };
            return this.clinicalEncounterService.uploadEncounterFile(params.tenantId, params.patientId, documentVo, file, documentTenantId);
          });

          return from(uploads$).pipe(mergeAll());
        }),
        catchError(() => {
          this.snackBar.open('File upload failed. Please try again.', 'Close', { duration: 4000 });
          return of(null);
        })
      );
  }
}
